import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FaMinus, FaSearch } from "react-icons/fa";
import NavbarAdmin from "../../Components/Admin/NavbarAdmin";
import Sidebar from "../../Components/Admin/Sidebar";
import useUsers from "../../hooks/Admin/useGetUsers";
import logo from "../../Assets/logo.png";

const Userlist = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("All");
  const [selectedUser, setSelectedUser] = useState(null);
  const [status, setStatus] = useState("Select");
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { users, loading, error, updateUserStatus } = useUsers();

  useEffect(() => {
    if (selectedUser) {
      setStatus(selectedUser.status);
    }
  }, [selectedUser]);

  const filteredUsers = users
    .filter((user) =>
      [user.fullname, user.email, user.contact, user.role]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .filter((user) => category === "All" || user.status === category);

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (selectedUser && newStatus !== selectedUser.status && newStatus !== "Select") {
      setStatus(newStatus);
      const result = await updateUserStatus({ ...selectedUser, status: newStatus });
      if (result.success) {
        alert("Status updated successfully.");
        setSelectedUser({ ...selectedUser, status: newStatus });
      } else {
        alert(`Failed to update: ${result.message}`);
        setStatus(selectedUser.status); // Revert status on failure
      }
    }
  };

  const handleSave = async () => {
    if (selectedUser && status !== "Select" && status !== selectedUser.status) {
      const result = await updateUserStatus({ ...selectedUser, status });
      if (result.success) {
        alert("User status saved successfully.");
        setSelectedUser({ ...selectedUser, status }); // Update local selected user
      } else {
        alert(`Failed to save: ${result.message}`);
        setStatus(selectedUser.status); // Revert status on failure
      }
    }
    setSelectedUser(null); // Close the details panel after saving
    setIsMinimized(false); // Reset minimization state
  };

  // Limit to 10 users by default
  const visibleUsersLimit = 10;
  const displayedUsers = showAll ? filteredUsers : filteredUsers.slice(0, visibleUsersLimit);

  if (error) return <p>Error: {error.message || "An error occurred"}</p>;

  return (
    <PageContainer>
      <Sidebar isOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
      <MainContent>
        <NavbarAdmin setIsSidebarOpen={setIsSidebarOpen} />
        <Content $isSidebarOpen={isSidebarOpen}>
          <TitleContainer>
            <Title>USER LIST</Title>
            <SearchContainer>
              <SearchBox>
                <input
                  type="text"
                  placeholder="Search user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FaSearch className="searchIcon" />
              </SearchBox>
              <CategorySelect>
                <select value={category} onChange={handleCategoryChange}>
                  <option value="All">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                </select>
              </CategorySelect>
            </SearchContainer>
          </TitleContainer>

          {loading ? (
            <LoadingContainer>
              <SpinnerContainer>
                <LogoSpinnerContainer>
                  <LoadingLogo src={logo} alt="Loading Logo" />
                  <InnerSpinner />
                </LogoSpinnerContainer>
                <LoadingText>LOADING</LoadingText>
              </SpinnerContainer>
            </LoadingContainer>
          ) : (
            <UserContainer>
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Document</th>
                      <th>Email</th>
                      <th>Contact</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsers.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", padding: "10px" }}>
                          No users found.
                        </td>
                      </tr>
                    ) : (
                      displayedUsers.map((user) => (
                        <UserRow
                          key={user.id}
                          onClick={() => {
                            setSelectedUser(user);
                            setIsMinimized(false);
                          }}
                          $isSelected={selectedUser?.id === user.id}
                        >
                          <td>{user.fullname}</td>
                          <td>
                            {user.documentBlob ? (
                              user.documentBlob.includes("data:image") ? (
                                <img
                                  src={user.documentBlob}
                                  alt="User Document"
                                  style={{ width: "100px", height: "auto" }}
                                />
                              ) : user.documentBlob.includes("data:application/pdf") ? (
                                <a href={user.documentBlob} target="_blank" rel="noopener noreferrer">
                                  View PDF
                                </a>
                              ) : (
                                <a href={user.documentBlob} download>
                                  Download Document
                                </a>
                              )
                            ) : (
                              "No document"
                            )}
                          </td>
                          <td>{user.email}</td>
                          <td>{user.contact}</td>
                          <td>{user.role}</td>
                          <td>{user.status}</td>
                        </UserRow>
                      ))
                    )}
                  </tbody>
                </Table>
                {filteredUsers.length > visibleUsersLimit && (
                  <ShowAllButton onClick={() => setShowAll(!showAll)}>
                    {showAll ? "Show Less" : "Show All Users"}
                  </ShowAllButton>
                )}
              </TableContainer>

              {selectedUser && !isMinimized && (
                <UserDetailContainer>
                  <MinimizeButton onClick={() => setSelectedUser(null)}>
                    <FaMinus />
                  </MinimizeButton>
                  <UserDetailForm>
                    <h3>Users Details</h3>
                    <label>
                      Name: <input type="text" value={selectedUser.fullname} readOnly />
                    </label>
                    <label>
                      Email: <input type="text" value={selectedUser.email} readOnly />
                    </label>
                    <label>
                      Contact: <input type="text" value={selectedUser.contact} readOnly />
                    </label>
                    <label>
                      Role: <input type="text" value={selectedUser.role} readOnly />
                    </label>
                    <label>
                      Status:
                      <select value={status} onChange={handleStatusChange}>
                        <option value="Select">Select</option>
                        <option value="Approved">Approved</option>
                        <option value="Pending">Pending</option>
                      </select>
                    </label>
                    <SaveButton onClick={handleSave}>SAVE</SaveButton>
                  </UserDetailForm>
                </UserDetailContainer>
              )}
            </UserContainer>
          )}
        </Content>
      </MainContent>
    </PageContainer>
  );
};

export default Userlist;

// Styled Components
const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding-bottom: 10px;
  margin-bottom: 20px;
`;

const ShowAllButton = styled.button`
  margin-top: 10px;
  background: none;
  border: none;
  color: #21551c;
  font-weight: bold;
  cursor: pointer;
  font-size: 16px;
  display: block;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    text-decoration: underline;
  }
`;



const UserRow = styled.tr`
  cursor: pointer;
  background-color: ${({ $isSelected }) => ($isSelected ? "#c8e6c9" : "white")};

  &:hover {
    background-color: #e0f2f1;
  }
`;

const PageContainer = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  background: #eaf4e1;
  overflow: hidden;
`;

const MainContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 20px;
  width: ${({ $isSidebarOpen }) => ($isSidebarOpen ? "calc(100% - 240px)" : "calc(100% - 100px)")};
  margin-left: ${({ $isSidebarOpen }) => ($isSidebarOpen ? "200px" : "60px")};
  transition: all 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const Title = styled.h1`
  color: #21551c;
  margin: 0;
  white-space: nowrap;
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: -3px;
  margin-bottom: 10px;
  width: 100%;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: white;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #21551c;
  width: 300px;

  input {
    border: none;
    flex: 1;
    padding: 8px;
    outline: none;
    width: 100%;
  }

  .searchIcon {
    color: #21551c;
    margin-right: 5px;
  }
`;

const CategorySelect = styled.div`
  select {
    padding: 12px;
    border: 1px solid #21551c;
    border-radius: 5px;
  }
`;

const UserContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
  flex: 2;
  min-width: 300px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    padding: 10px;
    border-bottom: 1px solid #ccc;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    background-color: #f4f4f4;
    color: #21551c;
  }
`;

const UserDetailContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 300px;
`;

const MinimizeButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #21551c;

  &:hover {
    color: darkgreen;
  }
`;

const UserDetailForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: white;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
  width: 100%;
  box-sizing: border-box;

  label {
    display: flex;
    flex-direction: column;
    font-weight: bold;
    color: #333;
    margin-top: 10px;
  }

  input,
  select {
    width: 93%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    margin-top: 6px;
  }

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 100%;
  }
`;

// Loading Placeholder Components
const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 65vh;
  background-color: transparent;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoSpinnerContainer = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
`;

const LoadingLogo = styled.img`
  width: 50px;
  height: 50px;
  z-index: 1;
  border-radius: 50%;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const InnerSpinner = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.5);
  border-top: 3px solid #004d00;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  pointer-events: none;
`;

const LoadingText = styled.span`
  color: #004d00;
  font-size: 1.2rem;
  font-weight: bold;

  &::after {
    content: ".";
    animation: dots 1.5s steps(3, end) infinite;
    margin-left: 5px;
  }

  @keyframes dots {
    0% {
      content: ".";
    }
    33% {
      content: "..";
    }
    66% {
      content: "...";
    }
    100% {
      content: ".";
    }
  }
`;
const SaveButton = styled.button`
  background-color: #21551c;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  margin: 0 auto; /* Center the button horizontally */
  width: fit-content;

  &:hover {
    background-color: #1a4316;
  }
`;